import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import useFetch from "@/lib/useFetch";
import { GlassCard, PageTitle, StatusBadge, LuxuryButton } from "@/components/lux";
import api, { money } from "@/lib/api";
import { toast } from "sonner";
import { Search, Eye, LogIn, Pencil, KeyRound } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export default function AdminUsers() {
  const [search, setSearch] = useState("");
  const { data, loading, reload } = useFetch(`/admin/users${search ? `?search=${search}` : ""}`, [search]);
  const [detail, setDetail] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [passwordOpen, setPasswordOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [newPassword, setNewPassword] = useState("");
  const nav = useNavigate();

  const view = async (mid) => {
    try { const r = await api.get(`/admin/users/${mid}`); setDetail(r.data); } catch { toast.error("Failed"); }
  };
  const setStatus = async (mid, status) => {
    try { await api.post(`/admin/users/${mid}/status?status=${status}`); toast.success(`Set ${status}`); reload(); if (detail) view(mid); }
    catch { toast.error("Failed"); }
  };
  const openEdit = () => {
    setForm({ name: detail.user.name || "", email: detail.user.email || "", phone: detail.user.phone || "" });
    setEditOpen(true);
  };
  const saveEdit = async () => {
    try {
      await api.patch(`/admin/users/${detail.user.member_id}/profile`, form);
      toast.success("User profile updated"); setEditOpen(false); await view(detail.user.member_id); reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const resetPassword = async () => {
    if (newPassword.length < 6) return toast.error("Password must be at least 6 characters");
    try {
      await api.post(`/admin/users/${detail.user.member_id}/reset-password`, { new_password: newPassword });
      toast.success("Password reset successfully"); setNewPassword(""); setPasswordOpen(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const loginAs = async (mid) => {
    try {
      const r = await api.post(`/admin/impersonate/${mid}`);
      localStorage.setItem("admin_token", localStorage.getItem("token"));
      localStorage.setItem("token", r.data.token);
      window.location.href = "/app";
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-6">
      <PageTitle title="User Management" subtitle="Search, inspect and manage members" right={
        <div className="flex items-center gap-2 rounded-xl border border-white/10 bg-white/5 px-3">
          <Search size={16} className="text-[var(--text-dim)]" />
          <input className="bg-transparent py-2 text-sm text-white outline-none" placeholder="Search name, email, ID" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="user-search" />
        </div>
      } />
      <GlassCard className="p-6">
        {loading ? <div className="skeleton h-40" /> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-[var(--text-dim)]"><tr className="border-b border-white/10">
                <th className="py-3">Member ID</th><th className="py-3">Name</th><th className="py-3">Email</th><th className="py-3">Contact</th><th className="py-3">Role</th><th className="py-3">Package</th><th className="py-3">Balance</th><th className="py-3">Status</th><th className="py-3"></th></tr></thead>
              <tbody>
                {data?.users?.map((u) => (
                  <tr key={u.id} className="row-hover border-b border-white/5" data-testid={`user-row-${u.member_id}`}>
                    <td className="py-3 text-white">{u.member_id}</td><td className="py-3">{u.name}</td>
                    <td className="py-3 text-[var(--text-dim)]">{u.email}</td><td className="py-3 text-[var(--text-dim)]">{u.phone || "—"}</td><td className="py-3 capitalize">{u.role?.replace("_", " ")}</td>
                    <td className="py-3 gold-text">${money(u.package)}</td><td className="py-3">${money(u.available)}</td>
                    <td className="py-3"><StatusBadge status={u.status} /></td>
                    <td className="py-3"><button onClick={() => view(u.member_id)} className="text-[var(--gold)]" data-testid={`user-view-${u.member_id}`}><Eye size={16} /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </GlassCard>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-2xl border-white/10 bg-[var(--bg-2)] text-white">
          <DialogHeader><DialogTitle>Member {detail?.user?.member_id}</DialogTitle></DialogHeader>
          {detail && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-3">
                <Info l="Name" v={detail.user.name} /><Info l="Email" v={detail.user.email} /><Info l="Contact" v={detail.user.phone || "—"} />
                <Info l="Rank" v={detail.user.rank} /><Info l="Status" v={detail.user.status} />
                <Info l="Sponsor" v={detail.sponsor || "—"} /><Info l="Package" v={`$${money(detail.user.package)}`} />
                <Info l="Available" v={`$${money(detail.user.balances.available)}`} />
                <Info l="Directs" v={detail.directs} /><Info l="Daily Salary" v={`$${money(detail.user.salary_daily)}`} />
              </div>
              <div className="rounded-xl glass-2 p-4">
                <p className="mb-2 text-xs uppercase text-[var(--text-dim)]">Business Volume</p>
                <div className="grid grid-cols-3 gap-2 text-sm">
                  <Info l="Left Real" v={`$${money(detail.business.left_real)}`} /><Info l="Right Real" v={`$${money(detail.business.right_real)}`} /><Info l="Matched" v={`$${money(detail.business.matched_total)}`} />
                  <Info l="Left Promo" v={`$${money(detail.business.left_virtual)}`} /><Info l="Right Promo" v={`$${money(detail.business.right_virtual)}`} /><Info l="Carry L/R" v={`$${money(detail.business.left_carry)} / $${money(detail.business.right_carry)}`} />
                </div>
              </div>
              {detail.cycle && (
                <div className="rounded-xl glass-2 p-4" data-testid="admin-cycle-info">
                  <p className="mb-2 text-xs uppercase text-[var(--text-dim)]">Active Earning Cycle</p>
                  <div className="grid grid-cols-3 gap-2 text-sm">
                    <Info l="Cycle #" v={detail.cycle.number} />
                    <Info l="Multiplier" v={`${detail.cycle.multiplier}X`} />
                    <Info l="Status" v={detail.cycle.status} />
                    <Info l="Cap" v={`$${money(detail.cycle.cap)}`} />
                    <Info l="Earned" v={`$${money(detail.cycle.earned)}`} />
                    <Info l="Remaining" v={`$${money(detail.cycle.remaining)}`} />
                  </div>
                </div>
              )}
              <div className="flex flex-wrap gap-2">
                <LuxuryButton size="sm" variant="gold" onClick={openEdit}><Pencil size={14} className="mr-1 inline" /> Edit Profile</LuxuryButton>
                <LuxuryButton size="sm" variant="dark" onClick={() => setPasswordOpen(true)}><KeyRound size={14} className="mr-1 inline" /> Reset Password</LuxuryButton>
                <LuxuryButton size="sm" variant="gold" onClick={() => loginAs(detail.user.member_id)} data-testid="admin-login-as"><LogIn size={14} className="mr-1 inline" /> Login as User</LuxuryButton>
                <LuxuryButton size="sm" onClick={() => setStatus(detail.user.member_id, "active")} data-testid="admin-activate">Activate</LuxuryButton>
                <LuxuryButton size="sm" variant="maroon" onClick={() => setStatus(detail.user.member_id, "suspended")} data-testid="admin-suspend">Suspend</LuxuryButton>
                <LuxuryButton size="sm" variant="dark" onClick={() => setStatus(detail.user.member_id, "pending")}>Set Pending</LuxuryButton>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-lg border-white/10 bg-[var(--bg-2)] text-white">
          <DialogHeader><DialogTitle>Edit User Profile</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Field l="Name"><input className={inp} value={form.name} onChange={e=>setForm({...form,name:e.target.value})}/></Field>
            <Field l="Email"><input className={inp} type="email" value={form.email} onChange={e=>setForm({...form,email:e.target.value})}/></Field>
            <Field l="Contact No."><input className={inp} value={form.phone} onChange={e=>setForm({...form,phone:e.target.value})}/></Field>
            <div className="flex justify-end gap-2"><LuxuryButton variant="dark" onClick={()=>setEditOpen(false)}>Cancel</LuxuryButton><LuxuryButton onClick={saveEdit}>Save Changes</LuxuryButton></div>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={passwordOpen} onOpenChange={setPasswordOpen}>
        <DialogContent className="max-w-lg border-white/10 bg-[var(--bg-2)] text-white">
          <DialogHeader><DialogTitle>Secure Password Reset</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-[var(--text-dim)]">Existing passwords are never displayed. Set a new password securely.</p>
            <input className={inp} type="password" placeholder="New password" value={newPassword} onChange={e=>setNewPassword(e.target.value)}/>
            <div className="flex justify-end"><LuxuryButton onClick={resetPassword}>Reset Password</LuxuryButton></div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
const inp = "w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white outline-none focus:border-[var(--gold)]/50";
const Field = ({ l, children }) => <div><p className="mb-1.5 text-xs uppercase tracking-wider text-[var(--text-dim)]">{l}</p>{children}</div>;
const Info = ({ l, v }) => <div><p className="text-xs text-[var(--text-dim)]">{l}</p><p className="text-white">{v}</p></div>;
